import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.admin.gen';
import { AuthProvider } from './lib/auth/AuthProvider';
import { supabase } from './lib/supabaseClient.admin';
import './styles/global.css';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Admin persists nothing client-side (plan.md "Offline" section: "The admin
// build persists nothing — short-lived KYC signed URLs and guest PII in a
// 24h cache are a liability with no upside"). No TanStack Query persister is
// wired here, and none should be added later without revisiting that
// decision. The admin Supabase client (lib/supabaseClient.admin.ts) also
// disables `persistSession`, so an admin tab always starts from a real
// sign-in rather than a silently-restored one. AuthProvider also clears
// this queryClient outright on sign-out or an account switch (fix round 1,
// item 2) — belt-and-braces with `persistSession: false`, since neither one
// alone covers every path (a tab left open across a sign-out still has a
// live in-memory cache until something clears it).
const queryClient = new QueryClient();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('entry-admin: #root element missing from index.html');

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider supabase={supabase} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
